import { CharStreams } from 'antlr4ts/CharStreams.js';
import { ExpressionLexer } from './generated/ExpressionLexer.js';
import { CommonTokenStream } from 'antlr4ts/CommonTokenStream.js';
import { ExpressionParser } from './generated/ExpressionParser.js';
import { AstVisitor } from './ast-visitor.js';
//Basic Parse
export function parseExpression(expression) {
    const lexer = new ExpressionLexer(CharStreams.fromString(expression));
    lexer.removeErrorListeners();
    const tokenStream = new CommonTokenStream(lexer);
    const parser = new ExpressionParser(tokenStream);
    parser.removeErrorListeners();
    const tree = parser.parse();
    const visitor = new AstVisitor();
    return visitor.visit(tree);
}
//Normalize Parse
const FLATTENABLE_OPERATORS = new Set([
    'addition',
    'multiplication',
    'and',
    'or',
]);
function isFlattenableOperator(operator) {
    return FLATTENABLE_OPERATORS.has(operator);
}
function flattenNestedVariadic(operator, operands) {
    const flattened = [];
    for (const operand of operands) {
        if (operand.type === 'variadic' && operand.operator === operator) {
            flattened.push(...operand.operands);
            continue;
        }
        if (operand.type === 'binary' && operand.operator === operator) {
            flattened.push(...flattenNestedVariadic(operator, [operand.leftHand, operand.rightHand]));
            continue;
        }
        // We preserve explicit grouping on purpose.
        // So `(T1 + T2) + T3` becomes:
        // variadic(addition, [group(paren, binary(addition, [T1, T2])), T3])
        // rather than erasing the group boundary.
        flattened.push(operand);
    }
    return flattened;
}
function buildFlattenedExpression(operator, operands) {
    const flattened = flattenNestedVariadic(operator, operands);
    if (flattened.length === 2) {
        return {
            type: 'binary',
            operator,
            leftHand: flattened[0],
            rightHand: flattened[1],
        };
    }
    return {
        type: 'variadic',
        operator,
        operands: flattened,
    };
}
function normalize(description) {
    switch (description.type) {
        case 'atom':
        case 'term':
        case 'number':
            return description;
        case 'unary':
            return {
                ...description,
                operand: normalize(description.operand),
            };
        case 'group':
            return {
                ...description,
                expression: normalize(description.expression),
            };
        case 'comparison':
        case 'binary': {
            const left = normalize(description.leftHand);
            const right = normalize(description.rightHand);
            if (isFlattenableOperator(description.operator)) {
                return buildFlattenedExpression(description.operator, [left, right]);
            }
            return {
                ...description,
                leftHand: left,
                rightHand: right,
            };
        }
        case 'variadic': {
            const operands = description.operands.map(normalize);
            return {
                ...description,
                operands: flattenNestedVariadic(description.operator, operands),
            };
        }
        case 'range':
            return {
                ...description,
                min: normalize(description.min),
                max: normalize(description.max),
            };
    }
}
export function parseExpression_Normalized(expression) {
    const parsed = parseExpression(expression);
    return normalize(parsed);
}
//# sourceMappingURL=parser.js.map